import { GameScreen } from "./GameScreen";
import { RenderUtil } from "./RenderUtil";

export type ResourceKind = "blood" | "souls" | "runes";

export interface StatsData {
    startedAt: number;
    endedAt: number | null;
    unitsCreated: Record<string, number>;
    enemiesDefeated: Record<string, number>;
    fireRainDestroyed: number;
    unitsConverted: number;
    resourcesGenerated: { blood: number; souls: number; runes: number };
}

export class Stats {

    startedAt: number;
    endedAt: number | null = null;
    unitsCreated: Record<string, number> = {};
    enemiesDefeated: Record<string, number> = {};
    fireRainDestroyed: number = 0;
    unitsConverted: number = 0;
    resourcesGenerated = { blood: 0, souls: 0, runes: 0 };

    constructor(startedAt: number) {
        this.startedAt = startedAt;
    }

    recordUnitCreated(unitId: string): void {
        if (this.endedAt !== null) return;
        this.unitsCreated[unitId] = (this.unitsCreated[unitId] ?? 0) + 1;
    }

    recordEnemyDefeated(unitId: string): void {
        if (this.endedAt !== null) return;
        this.enemiesDefeated[unitId] = (this.enemiesDefeated[unitId] ?? 0) + 1;
    }

    recordFireRainDestroyed(): void {
        if (this.endedAt !== null) return;
        this.fireRainDestroyed++;
    }

    recordConversion(): void {
        if (this.endedAt !== null) return;
        this.unitsConverted++;
    }

    recordResource(kind: ResourceKind, amount: number): void {
        if (this.endedAt !== null) return;
        this.resourcesGenerated[kind] += amount;
    }

    markEnded(now: number): void {
        if (this.endedAt === null) this.endedAt = now;
    }

    elapsedFormatted(): string {
        return RenderUtil.formatDuration(this.startedAt, this.endedAt ?? Date.now());
        // return (this.endedAt ?? now) - this.startedAt;
    }

    total(record: Record<string, number>): number {
        let n = 0;
        for (const key in record) n += record[key];
        return n;
    }

    score(): number {
        const created = this.total(this.unitsCreated);
        const defeated = this.total(this.enemiesDefeated);
        const nuked = this.fireRainDestroyed;
        const converted = this.unitsConverted;
        const res = this.resourcesGenerated;

        const base: number = defeated * 100
            + nuked * 10
            + converted * 10
            + created * 10
            + res.souls * 5
            + res.runes * 8
            + Math.floor(res.blood / 10);

        return base;
    }

    toData(): StatsData {
        return {
            startedAt: this.startedAt,
            endedAt: this.endedAt,
            unitsCreated: this.unitsCreated,
            enemiesDefeated: this.enemiesDefeated,
            fireRainDestroyed: this.fireRainDestroyed,
            unitsConverted: this.unitsConverted,
            resourcesGenerated: this.resourcesGenerated,
        };
    }

    static fromData(d: StatsData): Stats {
        const s = new Stats(d.startedAt);
        s.endedAt = d.endedAt ?? null;
        s.unitsCreated = d.unitsCreated ?? {};
        s.enemiesDefeated = d.enemiesDefeated ?? {};
        s.fireRainDestroyed = d.fireRainDestroyed ?? 0;
        s.unitsConverted = d.unitsConverted ?? 0;
        s.resourcesGenerated = d.resourcesGenerated ?? { blood: 0, souls: 0, runes: 0 };
        return s;
    }
}
